/**
 * Pain Management Overlay Component
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Slider } from "../ui/slider";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Activity, Save } from "lucide-react";
import { toast } from "sonner";
import SpeechDictation from "../SpeechDictation";

export function PainManagementOverlay({ patientId, patientName, onSave, onClose }) {
  const [formData, setFormData] = useState({
    pain_level: 5,
    pain_location: [],
    pain_quality: [],
    onset: "",
    interventions: [],
    effectiveness: "",
    notes: "",
  });

  const locations = ["Head", "Neck", "Chest", "Back", "Abdomen", "Hip", "Arm", "Leg", "Generalized"];
  const qualities = ["Sharp", "Dull", "Burning", "Aching", "Stabbing", "Throbbing", "Cramping"];
  const interventions = ["Medication", "Repositioning", "Heat", "Cold", "Massage", "Relaxation", "Distraction"];

  const toggleArrayItem = (arr, item) => {
    return arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item];
  };

  const handleSave = () => {
    toast.success("Pain assessment saved");
    if (onSave) onSave(formData);
    if (onClose) onClose();
  };

  return (
    <div className="space-y-4 p-4" data-testid="pain-management-overlay">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="w-5 h-5 text-red-400" />
        <h3 className="font-semibold text-white">Pain Management</h3>
        <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>
      </div>

      <div className="space-y-3">
        <Label className="text-slate-200">Pain Level (0-10)</Label>
        <div className="flex items-center gap-4">
          <Slider 
            value={[formData.pain_level]} 
            onValueChange={([v]) => setFormData({...formData, pain_level: v})} 
            max={10} 
            step={1} 
            className="flex-1" 
          />
          <Badge className={`${formData.pain_level <= 3 ? "bg-green-500" : formData.pain_level <= 6 ? "bg-yellow-500" : "bg-red-500"} text-white`}>
            {formData.pain_level}/10
          </Badge>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Pain Location(s)</Label>
        <div className="flex flex-wrap gap-1.5">
          {locations.map(loc => (
            <Badge 
              key={loc} 
              variant={formData.pain_location.includes(loc) ? "default" : "outline"} 
              className={`cursor-pointer transition-colors ${formData.pain_location.includes(loc) ? "bg-teal-600 text-white" : "border-slate-600 text-slate-300 hover:bg-slate-700"}`}
              onClick={() => setFormData({...formData, pain_location: toggleArrayItem(formData.pain_location, loc)})}
            >
              {loc}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Pain Quality</Label>
        <div className="flex flex-wrap gap-1.5">
          {qualities.map(q => (
            <Badge 
              key={q} 
              variant={formData.pain_quality.includes(q) ? "default" : "outline"} 
              className={`cursor-pointer transition-colors ${formData.pain_quality.includes(q) ? "bg-teal-600 text-white" : "border-slate-600 text-slate-300 hover:bg-slate-700"}`}
              onClick={() => setFormData({...formData, pain_quality: toggleArrayItem(formData.pain_quality, q)})}
            >
              {q}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Onset</Label>
        <Select value={formData.onset} onValueChange={(v) => setFormData({...formData, onset: v})}>
          <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="When did pain start?" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="new">New (within 24h)</SelectItem>
            <SelectItem value="recent">Recent (1-7 days)</SelectItem>
            <SelectItem value="chronic">Chronic (ongoing)</SelectItem>
            <SelectItem value="worsening">Worsening</SelectItem>
            <SelectItem value="improving">Improving</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Interventions Given</Label>
        <div className="flex flex-wrap gap-1.5">
          {interventions.map(i => (
            <Badge 
              key={i} 
              variant={formData.interventions.includes(i) ? "default" : "outline"} 
              className={`cursor-pointer transition-colors ${formData.interventions.includes(i) ? "bg-teal-600 text-white" : "border-slate-600 text-slate-300 hover:bg-slate-700"}`}
              onClick={() => setFormData({...formData, interventions: toggleArrayItem(formData.interventions, i)})}
            >
              {i}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Intervention Effectiveness</Label>
        <RadioGroup value={formData.effectiveness} onValueChange={(v) => setFormData({...formData, effectiveness: v})} className="text-slate-200">
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="effective" id="eff" className="border-slate-600" />
            <Label htmlFor="eff" className="text-slate-200">Effective - Pain reduced</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="partial" id="part" className="border-slate-600" />
            <Label htmlFor="part" className="text-slate-200">Partially effective</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="ineffective" id="ineff" className="border-slate-600" />
            <Label htmlFor="ineff" className="text-slate-200">Ineffective - No relief</Label>
          </div>
        </RadioGroup>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-slate-200">Additional Notes</Label>
          <SpeechDictation onTranscript={(t) => setFormData({...formData, notes: formData.notes + " " + t})} buttonSize="sm" />
        </div>
        <Textarea 
          value={formData.notes} 
          onChange={(e) => setFormData({...formData, notes: e.target.value})} 
          placeholder="Document pain details..." 
          rows={3} 
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
        <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700"><Save className="w-4 h-4 mr-1" />Save Assessment</Button>
      </div>
    </div>
  );
}

export default PainManagementOverlay;
